// ═══════════════════════════════════════════════════════════════
// V·IAnet Governance — AdminUsuariosPersonas (migrado al shell · prompt 0471 M10)
// Vincula cada usuario de la plataforma con una persona del directorio
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import {
  fetchAdminUsers,
  fetchPersonas,
  updateAdminUser,
  type PersonaResumen,
} from '../../../../shared/services/api';
import { useI18n } from '../../../../../i18n';
import {
  AdminPageHeader,
  AdminTableShell,
  type AdminTableColumn,
} from '../../../../shared/components/admin';

// ─── Types ──────────────────────────────────────────────────

interface AdminUser {
  id: string;
  email: string;
  nombreCompleto: string;
  rol: string;
  activo: boolean;
  personaId: string | null;
  persona: { id: string; nombre: string; apellidos: string; codigo: string } | null;
}

// ─── Component ──────────────────────────────────────────────

export function AdminUsuariosPersonas() {
  const { t } = useI18n();
  const adm = t.structure.admin;
  const admRec = adm as unknown as Record<string, string>;
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState('');
  const [linkModal, setLinkModal] = useState<AdminUser | null>(null);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetchAdminUsers(1, 100);
      setUsers((res.data ?? []) as AdminUser[]);
    } catch {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const handleDesvincular = async (user: AdminUser) => {
    try {
      await updateAdminUser(user.id, { personaId: null });
      showToast(`Persona desvinculada de ${user.email}`);
      loadUsers();
    } catch (err) {
      showToast(`Error: ${err instanceof Error ? err.message : 'desconocido'}`);
    }
  };

  const handleRolChange = async (user: AdminUser, newRol: string) => {
    try {
      await updateAdminUser(user.id, { rol: newRol });
      showToast(`Rol actualizado: ${user.email} → ${newRol}`);
      loadUsers();
    } catch (err) {
      showToast(`Error: ${err instanceof Error ? err.message : 'desconocido'}`);
    }
  };

  const handleVincular = async (user: AdminUser, persona: PersonaResumen) => {
    try {
      await updateAdminUser(user.id, { personaId: persona.id });
      setLinkModal(null);
      showToast(`Vinculacion guardada: ${user.email} → ${persona.nombre} ${persona.apellidos}`);
      loadUsers();
    } catch (err) {
      showToast(`Error: ${err instanceof Error ? err.message : 'desconocido'}`);
    }
  };

  const q = search.trim().toLowerCase();
  const filtered = q
    ? users.filter(
        (u) =>
          u.email.toLowerCase().includes(q) ||
          u.nombreCompleto.toLowerCase().includes(q) ||
          (u.persona
            ? `${u.persona.codigo} ${u.persona.nombre} ${u.persona.apellidos}`
                .toLowerCase()
                .includes(q)
            : false),
      )
    : users;

  const columns: AdminTableColumn<AdminUser>[] = [
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      sortKey: (r) => r.email,
      render: (r) => <span style={{ fontWeight: 500 }}>{r.email}</span>,
    },
    {
      key: 'nombreCompleto',
      label: admRec.colNombre ?? 'Nombre completo',
      sortable: true,
      sortKey: (r) => r.nombreCompleto,
    },
    {
      key: 'rol',
      label: admRec.rolPlataforma ?? 'Rol',
      render: (r) => <RolSelect rol={r.rol} onChange={(newRol) => handleRolChange(r, newRol)} />,
    },
    {
      key: 'persona',
      label: admRec.usuarioVinculado ?? 'Persona vinculada',
      sortable: true,
      sortKey: (r) => (r.persona ? `${r.persona.codigo}` : ''),
      render: (r) =>
        r.persona ? (
          <span style={{ color: '#16A34A', fontWeight: 500 }}>
            {r.persona.codigo} - {r.persona.nombre} {r.persona.apellidos}
          </span>
        ) : (
          <span style={{ color: '#94A3B8', fontStyle: 'italic' }}>
            {admRec.notLinked ?? '(sin vincular)'}
          </span>
        ),
    },
  ];

  return (
    <div>
      <AdminPageHeader
        title={admRec.usuarios ?? 'Usuarios ↔ Personas'}
        infoTooltip={{
          title: admRec.usuarios ?? 'Usuarios ↔ Personas',
          text: 'Vincula cada usuario de la plataforma con su ficha de persona en el directorio.\n\nLa vinculación es necesaria para que el usuario aparezca como responsable en procesos, documentos y órganos de gobierno.',
        }}
        search={{
          value: search,
          onChange: setSearch,
          placeholder: adm.search,
        }}
      />

      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 200,
            background: '#0E243E',
            color: '#fff',
            padding: '10px 20px',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {toast}
        </div>
      )}

      <AdminTableShell
        columns={columns}
        rows={filtered}
        rowKey={(u) => u.id}
        loading={loading}
        loadingMessage={t.common.cargando ?? 'Cargando…'}
        emptyMessage={adm.noResults}
        actionsLabel={admRec.acciones ?? 'Acción'}
        actions={(u) =>
          u.persona ? (
            <button onClick={() => handleDesvincular(u)} style={btnStyle('#DC2626', '#FEF2F2')}>
              {admRec.unlinkUser ?? 'Desvincular'}
            </button>
          ) : (
            <button onClick={() => setLinkModal(u)} style={btnStyle('#2F5496', '#EBF5FF')}>
              {admRec.linkUser ?? 'Vincular'}
            </button>
          )
        }
        footer={
          !loading ? (
            <span>
              {users.filter((u) => u.persona).length} / {users.length}
            </span>
          ) : undefined
        }
      />

      {linkModal && (
        <PersonaSearchModal
          user={linkModal}
          onSelect={(persona) => handleVincular(linkModal, persona)}
          onClose={() => setLinkModal(null)}
        />
      )}
    </div>
  );
}

// ─── RolSelect ──────────────────────────────────────────────

const ROLES = [
  'SUPERADMIN_PLATFORM',
  'TENANT_OWNER',
  'TENANT_ADMIN',
  'TENANT_AUDITOR',
  'MEMBER',
] as const;

const ROL_SELECT_COLORS: Record<string, string> = {
  SUPERADMIN_PLATFORM: '#0E243E',
  TENANT_OWNER: '#1F3864',
  TENANT_ADMIN: '#2F5496',
  TENANT_AUDITOR: '#1A94BF',
  MEMBER: '#6B7280',
};

function RolSelect({ rol, onChange }: { rol: string; onChange: (r: string) => void }) {
  const { t } = useI18n();
  const adm = t.structure.admin;
  const tooltips = (adm as unknown as { tooltips?: Record<string, string> }).tooltips ?? {};
  const color = ROL_SELECT_COLORS[rol] ?? '#6B7280';
  return (
    <select
      value={rol}
      onChange={(e) => {
        e.stopPropagation();
        onChange(e.target.value);
      }}
      onClick={(e) => e.stopPropagation()}
      title={tooltips.rolPlataforma ?? 'Rol de plataforma'}
      style={{
        fontSize: 12,
        fontWeight: 600,
        fontFamily: 'inherit',
        color,
        background: 'transparent',
        border: 'none',
        outline: 'none',
        cursor: 'pointer',
        padding: '2px 4px',
        borderRadius: 4,
      }}
    >
      {ROLES.map((r) => (
        <option key={r} value={r} style={{ color: ROL_SELECT_COLORS[r] ?? '#6B7280' }}>
          {r}
        </option>
      ))}
    </select>
  );
}

// ─── PersonaSearchModal ─────────────────────────────────────

interface PersonaSearchModalProps {
  user: AdminUser;
  onSelect: (persona: PersonaResumen) => void;
  onClose: () => void;
}

function PersonaSearchModal({ user, onSelect, onClose }: PersonaSearchModalProps) {
  const { t } = useI18n();
  const adm = t.structure.admin;
  const placeholders =
    (adm as unknown as { usuariosPlaceholders?: Record<string, string> }).usuariosPlaceholders ??
    {};
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PersonaResumen[]>([]);
  const [searching, setSearching] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const doSearch = useCallback(async (q: string) => {
    try {
      setSearching(true);
      const params: Record<string, string> = { porPagina: '20' };
      if (q.trim()) params.q = q.trim();
      const res = await fetchPersonas(params);
      setResults(res.data ?? []);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    doSearch('');
  }, [doSearch]);

  useEffect(() => {
    if (!loaded) return;
    const timer = setTimeout(() => doSearch(query), 300);
    return () => clearTimeout(timer);
  }, [query, doSearch, loaded]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 300,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          width: 480,
          maxHeight: '70vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #E5E7EB',
          }}
        >
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0E243E' }}>
            {(adm as unknown as Record<string, string>).linkUser ?? 'Vincular persona'}
          </h3>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#64748B' }}>
            <strong>{user.email}</strong>
          </p>
        </div>

        <div style={{ padding: '12px 20px 8px' }}>
          <input
            autoFocus
            type="text"
            placeholder={placeholders.busqueda ?? 'Buscar por nombre, codigo o email...'}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: 6,
              fontSize: 13,
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '4px 12px 12px' }}>
          {searching && !loaded ? (
            <div style={{ padding: 20, textAlign: 'center', color: '#94A3B8', fontSize: 13 }}>
              {t.common.cargando ?? 'Cargando…'}
            </div>
          ) : results.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: '#94A3B8', fontSize: 13 }}>
              {adm.noResults}
            </div>
          ) : (
            results.map((p) => (
              <button
                key={p.id}
                onClick={() => onSelect(p)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                  padding: '10px 12px',
                  border: 'none',
                  borderRadius: 6,
                  background: 'transparent',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: 13,
                  fontFamily: 'inherit',
                  transition: 'background 0.1s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#F0F4FF')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background: '#E0E7FF',
                    color: '#3730A3',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {p.nombre.charAt(0)}
                  {p.apellidos.charAt(0)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: '#0E243E' }}>
                    {p.nombre} {p.apellidos}
                  </div>
                  <div style={{ fontSize: 11, color: '#64748B' }}>
                    {p.codigo} {p.email ? `· ${p.email}` : ''}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>

        <div style={{ padding: '8px 20px 16px', textAlign: 'right' }}>
          <button
            onClick={onClose}
            style={{
              padding: '7px 16px',
              border: '1px solid #D1D5DB',
              borderRadius: 6,
              background: '#fff',
              fontSize: 13,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {t.common.cancelar}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────

function btnStyle(color: string, bg: string): React.CSSProperties {
  return {
    padding: '4px 12px',
    border: `1px solid ${color}30`,
    borderRadius: 6,
    background: bg,
    color,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
  };
}
